// src/Dashboard/pages/user/CalendarEvents/components/CalendarEventCard.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaCheckCircle,
  FaClock,
  FaHeart,
  FaInfoCircle,
  FaLock,
  FaMapMarkerAlt,
  FaRegHeart,
  FaUser,
  FaUserCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getAdherentsCountByEvent,
  getAdherentsByEvent,
  registerAdherent,
  unregisterAdherent,
} from "../../../../../services/eventServices";

import {
  getAdherentsByUser,
  getAllAdherentsList,
} from "../../../../../services/adherentServices";

import { sendNotification } from "../../../../../services/notificationServices";
import { getAdminsAndInstructorsIds } from "../../../../../services/userServices";

import {
  addUserEvent,
  deleteUserEvent,
} from "../../../../../services/usersEventsServices";

import { toAbsoluteEventPhotoUrl } from "../../../../../services/eventPhotoServices";
import { useUser } from "../../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../../utils/roleHelper";
import EventDetailsModalEnseignants from "../../../../../pages/PagesEnseignants/EventsPage/EventDetailsModalEnseignants";

import CalendarEventRegistrationPanel from "./CalendarEventRegistrationPanel";

import {
  formatDate,
  formatDateRange,
  getEventCategories,
  getStoredUserToken,
  isEventPast,
  normalizeCount,
  stripHtml,
  truncate,
} from "../utils/calendarEventsUtils";

const FALLBACK = "/img/no-image.jpg";

export default function CalendarEventCard({
  event,
  userId,
  isFavorite = false,
  onFavoriteChange,
}) {
  const { user } = useUser();

  const currentUserId = user?.userId || user?.id || userId;
  const userRole = getRoleFromRoleId(user?.role_id);

  const isAdminOrInstructor =
    [1, 3].includes(Number(user?.role_id)) ||
    ["admin", "enseignant"].includes(userRole);

  const [adherentsCount, setAdherentsCount] = useState(0);
  const [adherents, setAdherents] = useState([]);
  const [userAdherents, setUserAdherents] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("register");
  const [selectedAdherent, setSelectedAdherent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canRegister = !!Number(event?.can_register ?? 1);
  const eventIsPast = isEventPast(event);

  const imgSrc = useMemo(
    () => toAbsoluteEventPhotoUrl(event?.photo_url) || FALLBACK,
    [event?.photo_url]
  );

  const categories = useMemo(() => getEventCategories(event), [event]);

  const description = useMemo(() => {
    const text = stripHtml(event?.description || "");
    return truncate(text || "Aucune description disponible.", 170);
  }, [event?.description]);

  const loadData = useCallback(async () => {
    if (!event?.id) return;

    try {
      setLoadingData(true);

      const [countData, eventAdherents, myAdherents] = await Promise.all([
        getAdherentsCountByEvent(event.id),
        getAdherentsByEvent(event.id),
        isAdminOrInstructor
          ? getAllAdherentsList()
          : currentUserId
            ? getAdherentsByUser(currentUserId)
            : Promise.resolve([]),
      ]);

      setAdherentsCount(normalizeCount(countData));
      setAdherents(Array.isArray(eventAdherents) ? eventAdherents : []);
      setUserAdherents(Array.isArray(myAdherents) ? myAdherents : []);
    } catch (error) {
      console.error("Erreur chargement données événement :", error);
      toast.error("Erreur lors du chargement des inscriptions.");
    } finally {
      setLoadingData(false);
    }
  }, [currentUserId, event?.id, isAdminOrInstructor]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const userRegisteredAdherents = useMemo(() => {
    const myIds = new Set(userAdherents.map((adherent) => adherent.id));

    return adherents.filter((adherent) => {
      if (isAdminOrInstructor) return false;
      return myIds.has(adherent.id) && adherent.AdherentEvent;
    });
  }, [adherents, isAdminOrInstructor, userAdherents]);

  const eligibleForRegister = useMemo(() => {
    const alreadyIds = new Set(adherents.map((adherent) => adherent.id));

    return userAdherents
      .filter((adherent) => !alreadyIds.has(adherent.id))
      .sort((a, b) =>
        `${a.nom} ${a.prenom}`.localeCompare(
          `${b.nom} ${b.prenom}`,
          "fr",
          { sensitivity: "base" }
        )
      );
  }, [adherents, userAdherents]);

  const eligibleForUnregister = useMemo(() => {
    const allowedIds = new Set(userAdherents.map((adherent) => adherent.id));

    const list = isAdminOrInstructor
      ? adherents
      : adherents.filter((adherent) => allowedIds.has(adherent.id));

    return [...list].sort((a, b) =>
      `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, "fr", {
        sensitivity: "base",
      })
    );
  }, [adherents, isAdminOrInstructor, userAdherents]);

  const registrationStatus = useMemo(() => {
    if (userRegisteredAdherents.length === 0) return null;

    const validated = userRegisteredAdherents.some(
      (adherent) => adherent.AdherentEvent?.is_validate
    );

    return validated ? "validated" : "pending";
  }, [userRegisteredAdherents]);

  const handleFavoriteToggle = async () => {
    if (!userId) {
      toast.error("Utilisateur non identifié.");
      return;
    }

    const nextValue = !isFavorite;

    try {
      if (isFavorite) {
        await deleteUserEvent(userId, event.id);
      } else {
        await addUserEvent(userId, event.id);
      }

      onFavoriteChange?.(event.id, nextValue);

      toast.success(
        nextValue
          ? "Événement ajouté aux favoris."
          : "Événement retiré des favoris."
      );
    } catch (error) {
      console.error("Erreur favori événement :", error);
      toast.error("Impossible de mettre à jour les favoris.");
    }
  };

  const openPanelFor = (mode) => {
    const nextMode = mode === "register" && !canRegister ? "unregister" : mode;

    setPanelMode(nextMode);
    setSelectedAdherent("");
    setPanelOpen(true);

    if (mode === "register" && !canRegister) {
      toast.info(
        "Inscriptions closes pour cet événement. Désinscription possible."
      );
    }
  };

  const notifyAdmins = async (message) => {
    try {
      const recipients = await getAdminsAndInstructorsIds();
      const token = user?.token || getStoredUserToken();

      await Promise.all(
        (recipients || []).map((recipient) =>
          sendNotification(message, recipient.id, token).catch(() => null)
        )
      );
    } catch (error) {
      console.error("Notification admins échouée :", error);
    }
  };

  const confirmRegistration = async () => {
    if (!canRegister) {
      toast.info("Inscriptions closes pour cet événement.");
      return;
    }

    if (!selectedAdherent) {
      toast.error("Aucun adhérent sélectionné.");
      return;
    }

    try {
      setSubmitting(true);

      await registerAdherent(event.id, selectedAdherent);

      const adherent = userAdherents.find(
        (item) => item.id === Number(selectedAdherent)
      );

      if (adherent) {
        setAdherents((previous) => [
          ...previous,
          {
            ...adherent,
            AdherentEvent: {
              is_validate: false,
            },
          },
        ]);
      }

      setAdherentsCount((previous) => previous + 1);

      await notifyAdmins(
        `Inscription de ${adherent?.nom || ""} ${
          adherent?.prenom || ""
        } à l'événement : ${event.titre} du ${formatDate(event.start)}.`
      );

      toast.success("Inscription réussie.");
      setPanelOpen(false);
      setSelectedAdherent("");
    } catch (error) {
      console.error("Erreur inscription événement :", error);
      toast.error("Erreur lors de l'inscription.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmUnregister = async () => {
    if (!selectedAdherent) {
      toast.error("Veuillez sélectionner un adhérent.");
      return;
    }

    try {
      setSubmitting(true);

      await unregisterAdherent(selectedAdherent, event.id);

      const adherent =
        adherents.find((item) => item.id === Number(selectedAdherent)) ||
        userAdherents.find((item) => item.id === Number(selectedAdherent));

      setAdherents((previous) =>
        previous.filter((item) => item.id !== Number(selectedAdherent))
      );

      setAdherentsCount((previous) => Math.max(0, previous - 1));

      await notifyAdmins(
        `Désinscription de ${adherent?.nom || ""} ${
          adherent?.prenom || ""
        } de l'événement : ${event.titre} du ${formatDate(event.start)}.`
      );

      toast.success("Désinscription réussie.");
      setPanelOpen(false);
      setSelectedAdherent("");
    } catch (error) {
      console.error("Erreur désinscription événement :", error);
      toast.error("Erreur lors de la désinscription.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!event || event.is_active === false || event.is_active === 0) {
    return null;
  }

  return (
    <article className="group overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950">
      <div className="relative h-56 overflow-hidden bg-slate-100 dark:bg-slate-900">
        <img
          src={imgSrc}
          alt={event?.titre || "Événement"}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          onError={(imgEvent) => {
            imgEvent.currentTarget.src = FALLBACK;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-slate-950/20" />

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {eventIsPast ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-900/85 px-3 py-1 text-xs font-bold text-white shadow-sm">
              <FaClock />
              Passé
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
              <FaCheckCircle />
              À venir
            </span>
          )}

          {!canRegister && (
            <span className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white shadow-sm">
              <FaLock />
              Inscriptions closes
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleFavoriteToggle}
          className={`absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-2xl shadow-sm ring-1 transition ${
            isFavorite
              ? "bg-rose-500 text-white ring-rose-400"
              : "bg-white/90 text-slate-700 ring-white/70 hover:bg-white"
          }`}
          title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        >
          {isFavorite ? <FaHeart /> : <FaRegHeart />}
        </button>

        {registrationStatus && (
          <div className="absolute bottom-3 left-3">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold shadow-sm ${
                registrationStatus === "validated"
                  ? "bg-green-500 text-white"
                  : "bg-amber-400 text-slate-950"
              }`}
            >
              <FaUserCheck />
              {registrationStatus === "validated" ? "Inscrit" : "Pré-inscrit"}
            </span>
          </div>
        )}

        <div className="absolute bottom-3 right-3 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-800 shadow-sm">
          {formatDate(event?.start)}
        </div>
      </div>

      <div className="flex min-h-[360px] flex-col p-4">
        <div className="flex-1">
          <div className="mb-3 flex flex-wrap gap-2">
            {event?.level_event && (
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                {event.level_event}
              </span>
            )}

            {event?.type_event && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                {event.type_event}
              </span>
            )}
          </div>

          <h3 className="line-clamp-2 text-lg font-bold text-slate-900 dark:text-white">
            {event?.titre || "Événement sans titre"}
          </h3>

          <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {description}
          </p>

          <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <div className="flex items-start gap-2">
              <FaCalendarAlt className="mt-1 shrink-0 text-[#013E8E] dark:text-indigo-300" />
              <span>{formatDateRange(event)}</span>
            </div>

            <div className="flex items-start gap-2">
              <FaMapMarkerAlt className="mt-1 shrink-0 text-[#013E8E] dark:text-indigo-300" />
              <span>{event?.lieu_event || "Lieu à préciser"}</span>
            </div>

            <div className="flex items-center gap-2">
              <FaUser className="shrink-0 text-[#013E8E] dark:text-indigo-300" />
              <span>
                {loadingData ? "Chargement..." : `${adherentsCount} inscrit(s)`}
              </span>
            </div>
          </div>

          {categories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.slice(0, 4).map((category) => (
                <span
                  key={category}
                  className="rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-700"
                >
                  {category}
                </span>
              ))}

              {categories.length > 4 && (
                <span className="rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-700">
                  +{categories.length - 4}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 grid grid-cols-[1fr_auto_auto] gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
          <button
            type="button"
            onClick={() => openPanelFor(canRegister ? "register" : "unregister")}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-bold text-white transition ${
              canRegister
                ? "bg-[#013E8E] hover:bg-indigo-800"
                : "bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
            }`}
          >
            <FaCalendarCheck />
            {canRegister ? "Inscription" : "Désinscription"}
          </button>

          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
            title="Voir les détails"
          >
            <FaInfoCircle />
          </button>

          {event?.agenda_url ? (
            <a
              href={event.agenda_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
              title="Ajouter à l'agenda"
            >
              <FaCalendarAlt />
            </a>
          ) : (
            <span className="hidden" />
          )}
        </div>

        {panelOpen && (
          <CalendarEventRegistrationPanel
            event={event}
            canRegister={canRegister}
            panelMode={panelMode}
            setPanelMode={setPanelMode}
            selectedAdherent={selectedAdherent}
            setSelectedAdherent={setSelectedAdherent}
            eligibleForRegister={eligibleForRegister}
            eligibleForUnregister={eligibleForUnregister}
            submitting={submitting}
            onClose={() => setPanelOpen(false)}
            onConfirmRegistration={confirmRegistration}
            onConfirmUnregister={confirmUnregister}
          />
        )}
      </div>

      <EventDetailsModalEnseignants
        event={event}
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        adherents={adherents}
        refreshList={loadData}
      />
    </article>
  );
}